import { nanoid } from "@reduxjs/toolkit";

/** start | center | end, as the element's own Align control uses. */
export type PillAlignment = "start" | "center" | "end";

/**
 * The alignment the pills should take, read from the slide they are joining.
 *
 * Hardcoding "start" put left-aligned pills under a centred cover title —
 * "it should be aligned as the rest of the elements in that combo". The combo
 * is the thing that decides how its contents sit, so that is what is read,
 * with a text element beside the pills as the fallback.
 */
const ALIGNMENT_ALIASES: Record<string, PillAlignment> = {
  start: "start",
  left: "start",
  center: "center",
  centre: "center",
  middle: "center",
  end: "end",
  right: "end",
};

const toPillAlignment = (value: unknown): PillAlignment | null => {
  if (typeof value !== "string") return null;
  return ALIGNMENT_ALIASES[value.trim().toLowerCase()] ?? null;
};

/** The Document info pills element new stories carry on their first slide. */
export const createDocumentInfoElement = (
  horizontalAlignment: PillAlignment = "start",
) => ({
  id: nanoid(15),
  type: "element",
  settings: {
    name: "DocumentInfo",
    pills: ["sentBy", "readingTime"],
    size: "small",
    horizontalAlignment,
    // Sender resolves from the story's dynamic variables at read time.
    senderName: "{{sender_name}}",
    senderPicture: "{{sender_picture}}",
    anchorName: "",
    anchorTrigger: "",
  },
  children: [],
});

type SlideNode = {
  type?: string;
  settings?: { name?: string } & Record<string, unknown>;
  children?: SlideNode[];
};

const walk = (node: SlideNode, visit: (node: SlideNode) => void) => {
  visit(node);
  for (const child of node.children ?? []) walk(child, visit);
};

const hasDocumentInfo = (slide: SlideNode) => {
  let found = false;
  walk(slide, (node) => {
    if (node.type === "element" && node.settings?.name === "DocumentInfo") {
      found = true;
    }
  });
  return found;
};

const groupHasText = (group: SlideNode) =>
  (group.children ?? []).some(
    (child) => child.type === "element" && child.settings?.name === "RichText",
  );

/**
 * Index (in walk order) of the group the element should land in: the last
 * group that already holds text — the blank slide's paragraph position — or
 * the slide's last group. -1 when the slide has no group at all.
 */
const findTargetGroupIndex = (slide: SlideNode): number => {
  const groups: boolean[] = [];
  walk(slide, (node) => {
    if (node.type === "group") groups.push(groupHasText(node));
  });
  for (let index = groups.length - 1; index >= 0; index--) {
    if (groups[index]) return index;
  }
  return groups.length - 1;
};

/**
 * The slides array with the Document info pills appended to the first slide —
 * the same element blank stories are seeded with. Returns the input untouched
 * when the first slide already carries one or offers no group to put it in;
 * never mutates the caller's slides (the first slide is cloned).
 *
 * `spaceAbove` puts a spacer of that many px right above the pills, resizing
 * the one already there rather than stacking a second.
 */
export const withDocumentInfoOnFirstSlide = <T>(
  slides: T[],
  { spaceAbove }: { spaceAbove?: number } = {},
): T[] => {
  const first = slides[0] as SlideNode | undefined;
  if (!first) return slides;
  if (hasDocumentInfo(first)) {
    // Pills the slide already has — every library cover ships its own, after
    // a spacer of 14 to 82px — still get the space asked for.
    if (!spaceAbove) return slides;
    const clone = structuredClone(first);
    walk(clone, (node) => {
      const at = (node.children ?? []).findIndex(isDocumentInfo);
      if (at === -1 || !node.children) return;
      node.children = spacedAbove(node.children, at, spaceAbove);
    });
    return [clone as unknown as T, ...slides.slice(1)];
  }

  const targetIndex = findTargetGroupIndex(first);
  if (targetIndex === -1) return slides;

  const clone = structuredClone(first);
  // The alignment the slide already uses: the innermost combo that states one,
  // else a text element's. A group inherits, so it is not asked.
  let alignment: PillAlignment | null = null;
  walk(clone, (node) => {
    const stated = toPillAlignment(node.settings?.horizontalAlignment);
    if (!stated) return;
    if (node.type === "combo") alignment = stated;
    else if (!alignment && node.settings?.name === "RichText") alignment = stated;
  });

  let seen = -1;
  let done = false;
  walk(clone, (node) => {
    if (done || node.type !== "group") return;
    seen += 1;
    if (seen === targetIndex) {
      // THE WORDS RIGHT ABOVE THEM DECIDE FIRST. A cover's column of text set
      // to "text-align: left" inside a section set to center reads as left,
      // and the pills came out centred under it (Dawid, 2026-10-13: "if
      // everything is aligned to the left, this also should be aligned to the
      // left").
      const textAlign = (node.children ?? [])
        .filter((child) => child.settings?.name === "RichText")
        .map((child) => /text-align:\s*(left|center|right|start|end)/i.exec(String(child.settings?.content ?? ""))?.[1])
        .filter(Boolean)
        .pop();
      const children = [
        ...(node.children ?? []),
        createDocumentInfoElement(toPillAlignment(textAlign) ?? alignment ?? "start") as SlideNode,
      ];
      node.children = spaceAbove
        ? spacedAbove(children, children.length - 1, spaceAbove)
        : children;
      done = true;
    }
  });
  return [clone as unknown as T, ...slides.slice(1)];
};

const isDocumentInfo = (node: SlideNode) =>
  node.type === "element" && node.settings?.name === "DocumentInfo";

/**
 * `children` with a spacer of `px` right before the one at `at`: the spacer
 * already there resized, else a new one.
 */
const spacedAbove = (
  children: SlideNode[],
  at: number,
  px: number,
): SlideNode[] => {
  const before = children[at - 1];
  if (before?.type === "element" && before.settings?.name === "Separator") {
    return children.map((child, i) =>
      i === at - 1
        ? { ...child, settings: { ...child.settings, height: px } }
        : child,
    );
  }
  const spacer = {
    id: nanoid(15),
    type: "element",
    settings: { name: "Separator", height: px },
    children: [],
  } as SlideNode;
  return [...children.slice(0, at), spacer, ...children.slice(at)];
};

// `pills: null` means every pill is on (see `DocumentInfo.library` → PILL_ORDER).
const showsSender = (settings: NonNullable<SlideNode["settings"]>) =>
  !Array.isArray(settings.pills) || settings.pills.includes("sentBy");

/**
 * The nodes with `senderName` / `senderPicture` dropped from every Document
 * info element whose "Sent by" pill is off. The element keeps its
 * `{{sender_*}}` tokens whichever pills are on — the renderer only reads them
 * for the "Sent by" pill — so anything that scans the story for dynamic
 * variables (publish → `fs-vars` → Share dialog) must look at this view, or a
 * story showing nothing but reading time is treated as personalized. Never
 * mutates the input; untouched subtrees are returned by reference.
 */
export const omitHiddenSenderVariables = <T extends SlideNode>(
  nodes: T[],
): T[] => {
  let changed = false;
  const next = nodes.map((node) => {
    const out = omitHiddenSenderVariablesFromNode(node);
    if (out !== node) changed = true;
    return out;
  });
  return changed ? next : nodes;
};

const omitHiddenSenderVariablesFromNode = <T extends SlideNode>(node: T): T => {
  if (node.settings && isDocumentInfo(node) && !showsSender(node.settings)) {
    const {
      senderName: _name,
      senderPicture: _picture,
      ...settings
    } = node.settings;
    return { ...node, settings };
  }
  if (!node.children?.length) return node;
  const children = omitHiddenSenderVariables(node.children);
  return children === node.children ? node : { ...node, children };
};
